import { TRACKER_IDS, TRACKER_NAMES, type Payload, type WindowKey } from '../api/types';
import { gapStep, pct, usd } from '../lib/format';
import { HBars, finite } from '../ui/charts';
import { Count } from '../ui/motion';
import { openRowLink } from '../ui/rowLink';

export function Wallets({ data, win }: { data: Payload; win: WindowKey }) {
  const bal = data.analysis.balances;
  if (!bal || !bal.rows.length) {
    return <div className="tile"><p className="empty">No tracker published hot-wallet balances in this collection.</p></div>;
  }

  const trackers = TRACKER_IDS.filter((id) => bal.rows.some((r) => finite(r.by[id]?.value)));
  const rows = [...bal.rows].sort((x, y) => (y.consensus ?? 0) - (x.consensus ?? 0));
  const multi = rows.filter((r) => r.reportedBy.length >= 2);
  const gaps = multi
    .filter((r) => finite(r.spreadPct) && (r.consensus ?? 0) >= 250_000)
    .sort((x, y) => (y.spreadPct ?? 0) - (x.spreadPct ?? 0));
  const href = (key: string) => `/casino?c=${encodeURIComponent(key)}&w=${win}`;

  return (
    <div className="page">
      <section className="tile banner-head banner-wallets">
        <div className="page-head">
          <div>
            <h1>Hot wallets</h1>
            <p>What each tracker says the casinos are holding right now. A snapshot, not a flow: balances move every time a casino tops up or sweeps a wallet.</p>
          </div>
          <span className="status-badge">Point-in-time</span>
        </div>
        <div className="kpis">
          {trackers.map((id) => (
            <div key={id} className="kpi">
              <small><i className="swatch" style={{ background: `var(--tracker-${id})` }} aria-hidden />{TRACKER_NAMES[id]}</small>
              <b><Count value={bal.totals?.[id]?.value} format={usd} /></b>
              <span>{rows.filter((r) => finite(r.by[id]?.value)).length} casinos watched</span>
            </div>
          ))}
          <div className="kpi accent">
            <small>Cross-checkable</small>
            <b><Count value={multi.length} format={(v) => (finite(v) ? String(Math.round(v)) : '—')} /></b>
            <span>casinos on two or more trackers</span>
          </div>
        </div>
      </section>

      <div className="grid-2">
        <section className="tile">
          <div className="tile-head"><h2 className="tile-title">Largest holdings</h2><span className="tile-note">bar = median · dot = tracker</span></div>
          <HBars items={rows.slice(0, 10).map((r) => ({
            key: r.key, href: href(r.key), label: r.name, value: r.consensus ?? 0, display: usd(r.consensus),
            dots: trackers.flatMap((id) => {
              const v = r.by[id]?.value;
              return finite(v) ? [{ key: id, value: v, color: `var(--tracker-${id})`, title: `${TRACKER_NAMES[id]} ${usd(v)}` }] : [];
            }),
          }))} />
        </section>
        <section className="tile">
          <div className="tile-head"><h2 className="tile-title">Widest balance gaps</h2><span className="tile-note">holdings over $250K</span></div>
          <HBars items={gaps.slice(0, 10).map((r) => ({
            key: r.key, href: href(r.key), label: r.name, value: r.spreadPct ?? 0, display: pct(r.spreadPct, 0), className: `g${gapStep(r.spreadPct)}`,
          }))} />
          <p className="tile-foot">A tracker that has found more of a casino’s wallets sees more money in them. The wallet counts in the table below are the first place to look.</p>
        </section>
      </div>

      <section className="tile">
        <div className="tile-head"><h2 className="tile-title">Every balance</h2><span className="tile-note">{rows.length} casinos · wallets watched, where published</span></div>
        <div className="t-wrap">
          <table className="t">
            <thead>
              <tr>
                <th>Casino</th>
                {trackers.map((id) => <th key={id} className="r"><i className="swatch" style={{ background: `var(--tracker-${id})` }} aria-hidden /> {TRACKER_NAMES[id]}</th>)}
                <th className="r">Median</th>
                <th className="r">Apart</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 200).map((r) => (
                <tr key={r.key} className="row-link" onClick={openRowLink}>
                  <td><a className="plink" href={href(r.key)}>{r.name}</a></td>
                  {trackers.map((id) => {
                    const v = r.by[id]?.value;
                    const w = r.by[id]?.wallets;
                    return (
                      <td key={id} className="r">
                        {finite(v) ? usd(v) : <span className="muted">—</span>}
                        {finite(v) && finite(w) ? <small className="muted"> · {w}w</small> : null}
                      </td>
                    );
                  })}
                  <td className="r"><b>{usd(r.consensus)}</b></td>
                  <td className="r">{finite(r.spreadPct) ? <span className={`gap-pill g${gapStep(r.spreadPct)}`}>{pct(r.spreadPct, 0)}</span> : <span className="muted">one only</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
